import { formatRecoveryTime } from "@/lib/format";
import type { OperationsAssistantBriefing } from "@/types";

interface OperationsAIAssistantProps {
  briefing: OperationsAssistantBriefing | null;
  operatorName: string;
  loading?: boolean;
  onViewIncident?: (incidentId: string) => void;
}

function greetingPrefix(period: OperationsAssistantBriefing["greetingPeriod"], name: string): string {
  const label = { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening", night: "Good night" }[
    period
  ];
  return `${label} ${name}.`;
}

export function OperationsAIAssistant({ briefing, operatorName, loading, onViewIncident }: OperationsAIAssistantProps) {
  if (loading || !briefing) {
    return (
      <section className="ops-assistant glass-panel" aria-busy="true" aria-label="Operations AI Assistant">
        <p className="ops-assistant__loading">Analyzing platform state…</p>
      </section>
    );
  }

  const isCritical = briefing.platformStatus === "Critical" || briefing.customerImpact === "high";
  const showViewIncident = briefing.primaryIncidentId && onViewIncident;

  return (
    <section
      className={`ops-assistant glass-panel ${isCritical ? "ops-assistant--critical" : ""}`}
      aria-label="Operations AI Assistant"
    >
      <div className="ops-assistant__eyebrow">Operations AI Assistant</div>
      <p className="ops-assistant__greeting">{greetingPrefix(briefing.greetingPeriod, operatorName)}</p>

      <div className="ops-assistant__body">
        <p className="ops-assistant__line">
          <strong>Platform status:</strong> {briefing.platformStatus}.
        </p>

        {isCritical ? (
          <>
            <p className="ops-assistant__headline">{briefing.headline}</p>
            {briefing.tenantName ? (
              <p className="ops-assistant__line">
                <strong>Tenant:</strong> {briefing.tenantName}
              </p>
            ) : null}
            {briefing.causeSummary ? (
              <p className="ops-assistant__line">
                <strong>Cause:</strong> {briefing.causeSummary}
              </p>
            ) : null}
            {briefing.customerImpact === "high" ? (
              <p className="ops-assistant__line">
                <strong>Customer impact:</strong> High (or awaiting verification)
              </p>
            ) : briefing.customerImpact !== "none" ? (
              <p className="ops-assistant__line">
                <strong>Customer impact:</strong> {briefing.customerImpact}
              </p>
            ) : null}
            <p className="ops-assistant__line">
              <strong>Evidence status:</strong>{" "}
              {briefing.causeSummary || (briefing.detailLines?.length ?? 0) > 0
                ? "Evidence available"
                : "Confidence unavailable"}
            </p>
            {briefing.recommendedMitigation ? (
              <p className="ops-assistant__line">
                <strong>Recommended mitigation:</strong> {briefing.recommendedMitigation}
              </p>
            ) : null}
            {briefing.expectedRecoverySeconds != null ? (
              <p className="ops-assistant__line">
                <strong>Estimated recovery:</strong> {formatRecoveryTime(briefing.expectedRecoverySeconds)}
              </p>
            ) : null}
          </>
        ) : (
          <>
            {briefing.detailLines.map((line) => (
              <p key={line} className="ops-assistant__line">
                {line}
              </p>
            ))}
            {briefing.estimatedWorkMinutes ? (
              <p className="ops-assistant__line">
                <strong>Estimated work:</strong> {briefing.estimatedWorkMinutes} minute
                {briefing.estimatedWorkMinutes === 1 ? "" : "s"}.
              </p>
            ) : null}
          </>
        )}
      </div>

      {showViewIncident ? (
        <button
          type="button"
          className="btn btn-primary ops-assistant__review"
          onClick={() => onViewIncident(briefing.primaryIncidentId!)}
        >
          View Incident
        </button>
      ) : null}
    </section>
  );
}

/** @deprecated use OperationsAIAssistant */
export const AIOperator = OperationsAIAssistant;
